import { Fragment, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, PencilIcon } from '@heroicons/react/24/outline'
import { useQuery, useMutation } from '@apollo/client'
import { GET_ROLES } from '../graphql/queries'
import { UPDATE_USER } from '../graphql/mutations'
import { auth } from '../lib/auth'
import toast from 'react-hot-toast'

interface EditUserDialogProps {
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  user: {
    id: number
    email: string
    name: string
    phone_number?: string | null
    roles?: Array<{ id: number; role: string; description?: string | null }>
  } | null
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({ isOpen, onClose, onSaved, user }) => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([])

  const canAssignRoles = auth.hasPermission('user.assign_role')

  const { data: rolesData, loading: rolesLoading } = useQuery(GET_ROLES, {
    variables: { filters: { active: true } },
    skip: !isOpen || !canAssignRoles,
  })

  // Re-seed the form whenever the dialog opens for a (different) user
  useEffect(() => {
    if (isOpen && user) {
      setName(user.name)
      setEmail(user.email)
      setPhoneNumber(user.phone_number || '')
      setNewPassword('')
      setSelectedRoleIds((user.roles || []).map((r) => r.id))
    }
  }, [isOpen, user])

  const [updateUser, { loading: saving }] = useMutation(UPDATE_USER, {
    onCompleted: () => {
      toast.success('User updated successfully')
      onClose()
      onSaved()
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update user')
    },
  })

  const toggleRole = (roleId: number) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    // Only send fields that actually changed
    const updateData: Record<string, any> = {}
    if (name.trim() !== user.name) updateData.name = name.trim()
    if (email.trim().toLowerCase() !== user.email.toLowerCase()) updateData.email = email.trim()
    if ((phoneNumber.trim() || '') !== (user.phone_number || '')) updateData.phone_number = phoneNumber.trim()
    if (newPassword) updateData.password = newPassword

    if (canAssignRoles) {
      const currentIds = (user.roles || []).map((r) => r.id).sort((a, b) => a - b)
      const newIds = [...selectedRoleIds].sort((a, b) => a - b)
      if (JSON.stringify(currentIds) !== JSON.stringify(newIds)) {
        if (newIds.length === 0) {
          toast.error('Select at least one role')
          return
        }
        updateData.role_ids = newIds
      }
    }

    if (Object.keys(updateData).length === 0) {
      toast('No changes to save')
      return
    }

    updateUser({ variables: { id: user.id, updateData } })
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 flex items-center gap-2"
                  >
                    <PencilIcon className="h-5 w-5" />
                    Edit User
                  </Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      id="edit-name"
                      className="input-field"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>

                  <div>
                    <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email address
                    </label>
                    <input
                      type="email"
                      id="edit-email"
                      className="input-field"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>

                  <div>
                    <label htmlFor="edit-phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone number
                    </label>
                    <input
                      type="tel"
                      id="edit-phone"
                      className="input-field"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                  </div>

                  <div>
                    <label htmlFor="edit-password" className="block text-sm font-medium text-gray-700 mb-1">
                      New password <span className="text-gray-400 font-normal">(leave empty to keep current)</span>
                    </label>
                    <input
                      type="password"
                      id="edit-password"
                      className="input-field"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>

                  {canAssignRoles && (
                    <div>
                      <span className="block text-sm font-medium text-gray-700 mb-1">Roles</span>
                      {rolesLoading ? (
                        <p className="text-sm text-gray-500">Loading roles…</p>
                      ) : (
                        <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3">
                          {(rolesData?.roles || []).map((role: any) => (
                            <label key={role.id} className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                className="mt-1"
                                checked={selectedRoleIds.includes(role.id)}
                                onChange={() => toggleRole(role.id)}
                              />
                              <span>
                                <span className="block text-sm font-medium text-gray-900">{role.role}</span>
                                {role.description && (
                                  <span className="block text-xs text-gray-500">{role.description}</span>
                                )}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-2">
                    <button type="button" className="btn-secondary" onClick={onClose}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={saving}>
                      {saving ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default EditUserDialog
